package com.hyperether.goodjob.scenes.register

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.material.Button
import androidx.compose.material.ButtonDefaults
import androidx.compose.material.Icon
import androidx.compose.material.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.TextUnit
import com.hyperether.goodjob.Dp
import com.hyperether.goodjob.Sp
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.apple
import com.hyperether.goodjob.resources.fb
import com.hyperether.goodjob.resources.google
import com.hyperether.goodjob.resources.password_length
import com.hyperether.goodjob.resources.password_not_visible
import com.hyperether.goodjob.resources.password_visible
import com.hyperether.goodjob.resources.passwords_do_not_match
import com.hyperether.goodjob.scenes.components.pointerCursorIfEnabled
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.InputBorderColor
import com.hyperether.goodjob.theme.InputIconColor
import com.hyperether.goodjob.theme.RegisterBorder
import com.hyperether.goodjob.theme.TextColor
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource

@Composable
fun AppInputField(
    text: MutableState<String>,
    label: String? = null,
    placeholder: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    paddingTop: androidx.compose.ui.unit.Dp = 10.toScaleDp(),
    isMaxLinesDefined: Boolean = true,
    isEmail: Boolean = false
) {
    Column(
        modifier = modifier.padding(top = paddingTop)
            .fillMaxWidth()
    ) {
        OutlinedTextField(
            modifier = if (getPlatformChecker().isMobile()) Modifier.fillMaxWidth() else Modifier
                .height(56.toScaleDp())
                .fillMaxWidth(),
            value = text.value,
            onValueChange = { newValue ->
                if (enabled && newValue.count { it == '\n' } < 1) {
                    if (isEmail)
                        text.value = newValue.trim()
                    else
                        text.value = newValue
                }
            },
            singleLine = true,
            textStyle = TextStyle(fontSize = 14.toScaleSp()),
            label = label?.let {
                {
                    Text(
                        text = it,
                        fontSize = 14.toScaleSp(),
                        color = if (enabled) TextColor else Color.Gray,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            },
            placeholder = {
                Text(
                    text = placeholder,
                    fontSize = 14.toScaleSp(),
                    color = Color.LightGray,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    style = TextStyle(fontSize = 14.toScaleSp(), lineHeight = 20.toScaleSp())
                )
            },
            enabled = enabled,
            maxLines = if (isMaxLinesDefined) 1 else Int.MAX_VALUE,
            colors = OutlinedTextFieldDefaults.colors(
                disabledBorderColor = InputBorderColor,
                unfocusedBorderColor = InputBorderColor,
                disabledTextColor = TextColor,
                focusedBorderColor = BlueApp,
                cursorColor = BlueApp
            ),
        )
    }
}

@Composable
fun ZipInputField(
    text: MutableState<String>,
    label: String,
    placeholder: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    Box(
        modifier = if (getPlatformChecker().isMobile()) modifier
            .fillMaxWidth()
        else modifier
            .fillMaxWidth()
            .height(56.toScaleDp())
    ) {
        OutlinedTextField(
            modifier = Modifier.fillMaxSize(),
            value = text.value,
            onValueChange = { newValue ->
                if (enabled && newValue.all { it.isDigit() } && newValue.length <= 12) {
                    text.value = newValue
                }
            },
            singleLine = true,
            textStyle = TextStyle(fontSize = 14.toScaleSp()),
            label = {
                Text(
                    text = label,
                    fontSize = 14.toScaleSp(),
                    color = if (enabled) TextColor else Color.Gray,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            placeholder = {
                Text(
                    text = placeholder,
                    fontSize = 14.toScaleSp(),
                    color = Color.LightGray,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            enabled = enabled,
            maxLines = 1,
            keyboardOptions = KeyboardOptions.Default.copy(
                keyboardType = KeyboardType.Number
            ),
            colors = OutlinedTextFieldDefaults.colors(
                disabledBorderColor = InputBorderColor,
                unfocusedBorderColor = InputBorderColor,
                disabledTextColor = TextColor,
                focusedBorderColor = BlueApp,
                cursorColor = BlueApp
            )
        )
    }
}

@Composable
fun EstimatedDurationInputField(
    text: MutableState<String>,
    label: String,
    placeholder: String,
    modifier: Modifier = Modifier
) {
    val maxDuration = 7200
    Box(
        modifier = modifier
            .fillMaxWidth()
            .padding(top = 10.toScaleDp())
    ) {

        OutlinedTextField(
            modifier = Modifier.fillMaxWidth().height(56.toScaleDp()),
            value = text.value,
            onValueChange = {
                if (it.isEmpty() || (it.toIntOrNull() != null && it.toInt() in 0..maxDuration)) {
                    text.value = it
                }
            },
            textStyle = TextStyle(fontSize = 14.toScaleSp()),
            label = {
                Text(
                    label, fontSize = 14.toScaleSp(), color = TextColor,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            placeholder = {
                Text(
                    placeholder, fontSize = 14.toScaleSp(),
                    color = Color.LightGray,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    style = TextStyle(fontSize = 14.toScaleSp(), lineHeight = 20.toScaleSp())
                )
            },
            keyboardOptions = KeyboardOptions.Default.copy(keyboardType = KeyboardType.Number),
            colors = OutlinedTextFieldDefaults.colors(
                disabledBorderColor = InputBorderColor,
                unfocusedBorderColor = InputBorderColor,
                disabledTextColor = TextColor,
                focusedBorderColor = BlueApp,
                cursorColor = BlueApp
            )
        )

    }
}

@Composable
fun SignInInputField(
    text: String,
    onTextChanged: (String) -> Unit,
    placeholder: String,
    isError: Boolean = false,
    errorMessage: String? = null
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            value = text,
            onValueChange = onTextChanged,
            placeholder = {
                Text(
                    placeholder, fontSize = 14.toScaleSp(),
                    color = Color.LightGray,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            isError = isError,
            singleLine = true,
            supportingText = {
                if (isError && errorMessage != null) {
                    Text(
                        text = errorMessage,
                        color = Color.Red,
                        fontSize = 12.toScaleSp()
                    )
                }
            },
            colors = OutlinedTextFieldDefaults.colors(
                disabledBorderColor = InputBorderColor,
                unfocusedBorderColor = InputBorderColor,
                disabledTextColor = TextColor,
                focusedBorderColor = BlueApp,
                cursorColor = BlueApp
            )
        )
    }
}

@Composable
fun RegisterInputFieldWeb(
    text: String,
    onTextChanged: (String) -> Unit,
    placeholder: String,
    isError: Boolean = false,
    drawableResource: DrawableResource,
    modifier: Modifier = Modifier,
    isSpaceAllowed: Boolean = false,
    errorMessage: String? = null
) {
    OutlinedTextField(
        modifier = Modifier.fillMaxWidth()
            .defaultMinSize(minHeight = 50.toScaleDp())
            .then(modifier),
        value = text,
        singleLine = true,
        isError = isError,
        textStyle = TextStyle(fontSize = 14.toScaleSp()),
        onValueChange = { newValue ->
            onTextChanged(if (isSpaceAllowed) newValue.replace("\t", "") else newValue.trim())
        },
        placeholder = {
            Text(
                color = Color.LightGray,
                text = placeholder,
                maxLines = 1,
                fontSize = 14.toScaleSp(),
                overflow = TextOverflow.Ellipsis
            )
        },
        supportingText = {
            if (isError && errorMessage != null) {
                Text(
                    text = errorMessage,
                    color = Color.Red,
                    fontSize = 12.toScaleSp()
                )
                Spacer(modifier = Modifier.height(8.toScaleDp()))
            }
        },
        leadingIcon = {
            Box(modifier = Modifier.padding(horizontal = 12.toScaleDp())) {
                Image(
                    painter = painterResource(drawableResource),
                    contentDescription = null,
                    modifier = Modifier.size(20.toScaleDp())
                )
            }
        },
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = BlueApp,
            unfocusedBorderColor = RegisterBorder,
            disabledTextColor = TextColor
        ),
        shape = RoundedCornerShape(12.toScaleDp())
    )
}

@Composable
fun RegisterPasswordInputFieldWeb(
    text: MutableState<String>,
    label: String,
    drawableResource: DrawableResource,
    focusManager: FocusManager,
    isRegister: Boolean = false,
    showMismatchError: Boolean = false,
    modifier: Modifier = Modifier,
) {
    var passVisible by remember { mutableStateOf(false) }
    val isTooShort = text.value.length in 1..7

    Column {
        OutlinedTextField(
            modifier = Modifier
                .fillMaxWidth()
                .defaultMinSize(minHeight = 50.toScaleDp())
                .then(modifier),
            value = text.value,
            onValueChange = {
                text.value = it.trim()
            },
            textStyle = TextStyle(fontSize = 14.toScaleSp()),
            singleLine = true,
            placeholder = {
                Text(
                    color = Color.LightGray,
                    text = label,
                    maxLines = 1,
                    fontSize = 14.toScaleSp(),
                    overflow = TextOverflow.Ellipsis
                )
            },
            isError = isTooShort && isRegister,
            leadingIcon = {
                Box(modifier = Modifier.padding(horizontal = 12.toScaleDp())) {
                    Image(
                        painter = painterResource(drawableResource),
                        contentDescription = null,
                        modifier = Modifier.size(20.toScaleDp())
                    )
                }
            },
            visualTransformation = if (passVisible) VisualTransformation.None else PasswordVisualTransformation(),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
            trailingIcon = {
                val image =
                    if (passVisible) Res.drawable.password_visible else Res.drawable.password_not_visible

                Icon(
                    painter = painterResource(image),
                    contentDescription = "Show/Hide password",
                    tint = InputIconColor,
                    modifier = Modifier
                        .size(16.toScaleDp())
                        .focusable(false)
                        .clickable { passVisible = !passVisible }
                        .pointerHoverIcon(PointerIcon.Hand)
                )
            },
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = if (isTooShort && isRegister) Color.Red else BlueApp,
                unfocusedBorderColor = if (isTooShort && isRegister) Color.Red else RegisterBorder,
                disabledTextColor = TextColor
            ),
            shape = RoundedCornerShape(12.toScaleDp())
        )

        if (isTooShort) {
            Text(
                text = stringResource(Res.string.password_length),
                color = Color.Red,
                fontSize = 12.toScaleSp(),
                modifier = Modifier.padding(start = 8.toScaleDp(), top = 4.toScaleDp())
            )
        } else if (showMismatchError) {
            Text(
                text = stringResource(Res.string.passwords_do_not_match),
                color = Color.Red,
                fontSize = 12.toScaleSp(),
                modifier = Modifier.padding(start = 8.toScaleDp(), top = 4.toScaleDp())
            )
        }
    }
}

@Composable
fun FilledButton(
    onClick: () -> Unit,
    isEnabled: Boolean = true,
    text: String,
    modifier: Modifier = Modifier,
    color: Color = BlueApp,
    letterSpacing: TextUnit = TextUnit.Unspecified
) {
    Button(
        onClick = onClick,
        modifier = modifier
            .fillMaxWidth()
            .height(50.toScaleDp())
            .clip(RoundedCornerShape(25.toScaleDp()))
            .pointerCursorIfEnabled(isEnabled),
        colors = ButtonDefaults.buttonColors(color),
        enabled = isEnabled
    ) {
        DisableSelection {
            Text(
                text = text,
                color = Color.White,
                fontSize = 15.toScaleSp(),
                fontWeight = FontWeight.Normal,
                letterSpacing = letterSpacing,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
fun FilledButtonHeader(
    onClick: () -> Unit,
    isEnabled: Boolean = true,
    text: String,
    modifier: Modifier = Modifier,
    color: Color = BlueApp,
    letterSpacing: TextUnit = TextUnit.Unspecified
) {
    Button(
        onClick = onClick,
        modifier = modifier
            .height(50.toScaleDp())
            .clip(RoundedCornerShape(25.toScaleDp()))
            .pointerCursorIfEnabled(isEnabled),
        colors = ButtonDefaults.buttonColors(color),
        enabled = isEnabled
    ) {
        DisableSelection {
            Text(
                text = text,
                color = Color.White,
                fontSize = 15.toScaleSp(),
                fontWeight = FontWeight.Normal,
                letterSpacing = letterSpacing,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
fun RegisterWithButton(
    onClick: () -> Unit,
    isEnabled: Boolean = true,
    text: String,
    modifier: Modifier = Modifier,
    drawableResource: DrawableResource
) {
    OutlinedButton(
        onClick = onClick,
        border = BorderStroke(Dp(1f), Color(0xFFE6E8E7)),
        modifier = modifier
            .height(Dp(54f))
            .fillMaxWidth(),
        shape = RoundedCornerShape(Dp(25f)),
        colors = ButtonDefaults.outlinedButtonColors(
            backgroundColor = Color.White,
            contentColor = Color(0xFF313030)
        ),
        enabled = isEnabled
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
            modifier = Modifier.fillMaxWidth()
        ) {
            androidx.compose.material.Text(
                text = text,
                color = Color(0xFF313030),
                fontSize = Sp(16f),
                fontWeight = FontWeight.Normal
            )
            Spacer(modifier = Modifier.width(Dp(8f)))
            Image(
                painter = painterResource(drawableResource),
                contentDescription = null,
                modifier = Modifier
                    .size(Dp(24f)),
                alignment = Alignment.CenterEnd
            )
        }
    }
}

@Composable
fun IconButtonRow(
    onFbClick: () -> Unit,
    onGoogleClick: () -> Unit,
    onAppleClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = Dp(30f)),
        horizontalArrangement = Arrangement.SpaceEvenly,
        verticalAlignment = Alignment.CenterVertically
    ) {
        val icons = listOf(
            Res.drawable.fb to onFbClick,
            Res.drawable.google to onGoogleClick,
            Res.drawable.apple to onAppleClick
        )

        for ((icon, clickAction) in icons) {
            Box(
                modifier = Modifier
                    .width(Dp(100f))
                    .height(Dp(55f))
                    .border(Dp(1f), Color.Gray, RoundedCornerShape(Dp(8f)))
                    .background(Color.Transparent)
                    .clickable { clickAction() },
                contentAlignment = Alignment.Center
            ) {
                Image(
                    painter = painterResource(icon),
                    contentDescription = "Icon",
                    modifier = Modifier.size(Dp(24f))
                )
            }
        }
    }
}